import { useMemo, useState } from 'react';
import { Loader2, Plus, Undo2, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { useToast } from '../../../contexts/ToastContext';
import { type AdminUser } from '../../../api/extensions/useAdminOps';
import {
  useCreateAdminAdjustment,
  useDeleteAdminAdjustment,
  useAdminUserStripePayments,
  useCreateAdminStripeRefund,
  type AdjustmentType,
  type StripePaymentRow,
} from '../../../api/extensions/useAdminFinancials';
import { type TransactionItem } from '../../../api/extensions/useTransactionHistory';
import { useOutstandingBalance } from '../../../api/extensions/useRegistrationOps';
import { buildStripeDashboardUrl } from '../../../utils/stripeDashboardUrl';
import { TransactionHistoryTab } from '../../profile/TransactionHistoryTab';
import { ManualChargesCard } from '../../profile/PaymentsTab';

const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';

// User-facing labels and colorways for each ledger entry type. Kept tightly
// scoped — used by the Record-entry modal's type picker.
const TYPE_META: Record<AdjustmentType, { label: string; cls: string; description: string }> = {
  charge: {
    label: 'Charge',
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    description: 'Add to amount due (e.g. committee fronted an entry fee).',
  },
  refund: {
    label: 'Refund',
    cls: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
    description: 'Manual refund outside Stripe (e.g. cash refund).',
  },
  adjustment: {
    label: 'Adjustment',
    cls: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30',
    description: 'Custom debit or credit. You pick the direction.',
  },
  comp: {
    label: 'Comp',
    cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    description: 'Comp the entry (full credit, no out-of-pocket cost).',
  },
  credit: {
    label: 'Credit',
    cls: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
    description: 'Account credit on file (e.g. overpayment to apply later).',
  },
};

function formatCurrency(amount: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

interface FinancialsTabProps {
  user: AdminUser;
  /** Retained for the UserDetailsPage call site; ledger entries are always
   *  org-scoped now, so it no longer affects the Record-entry modal. */
  isAllOrgAdmin?: boolean;
}

/**
 * Admin Financials tab.
 *
 * The transaction DISPLAY is the exact same component the contestant sees on
 * their own Profile → Financials (`TransactionHistoryTab`), fed this user's id
 * — so there's one consolidated look (balance analytics + unified ledger of
 * Stripe payments/refunds, cash/check receipts, manual ledger entries, and
 * pending fees) instead of a divergent bespoke ledger + Stripe-activity UI.
 *
 * What stays admin-only is the ACTIONS, surfaced in the toolbar above the
 * display: Record a manual ledger entry, and Refund via Stripe. Stripe
 * references in the ledger also link out to the Stripe dashboard for admins
 * (contestants see the same reference text, just not linked).
 */
export default function FinancialsTab({ user }: FinancialsTabProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [showStripeRefund, setShowStripeRefund] = useState(false);
  // The manual ledger row the admin has chosen to delete; null = no dialog.
  const [pendingDelete, setPendingDelete] = useState<TransactionItem | null>(
    null,
  );

  const orgs = useMemo(
    () =>
      user.organization_memberships
        .filter((m) => m.organization_id !== ALL_ORG_ID)
        .map((m) => ({
          id: m.organization_id,
          name: m.organization.short_name || m.organization.name,
        })),
    [user.organization_memberships],
  );

  // Drives the Refund-via-Stripe modal's payment picker (live
  // refundable_amount, computed server-side). Independent of the display feed.
  const { data: stripePaymentsData } = useAdminUserStripePayments(user.id);
  const stripePayments = stripePaymentsData?.payments ?? [];

  // This contestant's outstanding balance — feeds the SAME Organization-ledger
  // card the contestant sees on their own Profile → Financials, so the two
  // views agree. Read-only here (admins settle via the toolbar above).
  const { data: outstanding } = useOutstandingBalance(user.id);

  // Any payment with refundable_amount > $0.01 is eligible for a Stripe-side
  // refund. Floor at one penny because Stripe's minimum partial refund is 1¢
  // and floating-point drift can otherwise leave $0.001 visible as available.
  const refundablePayments = useMemo(
    () => stripePayments.filter((p) => p.refundable_amount > 0.01),
    [stripePayments],
  );

  return (
    <div className="space-y-6">
      {/* Admin actions toolbar. The manual ledger is append-only — to undo,
          record a reversing entry. */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold">Financials</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              The same transaction history this contestant sees, plus admin-only
              actions. Record a manual charge / refund / comp / credit, or issue
              a Stripe refund. The manual ledger is append-only — to undo, record
              a reversing entry.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowStripeRefund(true)}
              disabled={refundablePayments.length === 0}
              title={
                refundablePayments.length === 0
                  ? 'No Stripe payments left to refund against'
                  : 'Issue a Stripe refund against a prior payment'
              }
            >
              <Undo2 className="h-4 w-4 mr-1.5" /> Refund via Stripe
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> Record entry
            </Button>
          </div>
        </div>
      </div>

      {/* Organization ledger — the SAME per-org charges/credits/credit-applied/
          paid/balance card the contestant sees, read-only (the admin settles
          via the toolbar above, not the contestant's Stripe checkout). */}
      <ManualChargesCard
        manualCharges={outstanding?.manual_charges}
        userId={user.id}
        readOnly
      />

      {/* Shared transaction display — identical to the contestant's own
          Financials tab, with admin-only Stripe dashboard links + the
          delete affordance on manual ledger rows. */}
      <TransactionHistoryTab
        userId={user.id}
        showStripeDashboardLinks
        onDeleteAdjustment={setPendingDelete}
      />

      {pendingDelete && (
        <DeleteAdjustmentDialog
          userId={user.id}
          row={pendingDelete}
          onClose={() => setPendingDelete(null)}
        />
      )}

      {showCreate && (
        <CreateAdjustmentModal
          userId={user.id}
          orgs={orgs}
          onClose={() => setShowCreate(false)}
        />
      )}

      {showStripeRefund && (
        <RefundViaStripeModal
          userId={user.id}
          payments={refundablePayments}
          onClose={() => setShowStripeRefund(false)}
        />
      )}
    </div>
  );
}

/**
 * Confirm + delete a manual ledger entry. Driven off the shared transaction
 * feed row (kind='adjustment'), whose `reference` IS the account_adjustments
 * id and whose signed `amount` (debit +, credit −) and `reason` give us
 * everything the confirm copy needs — no extra fetch. Settled entries never
 * reach here (the trash button is disabled upstream); the backend also
 * rejects them with a 409 as a second line of defense.
 */
function DeleteAdjustmentDialog({
  userId,
  row,
  onClose,
}: {
  userId: string;
  row: TransactionItem;
  onClose: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const del = useDeleteAdminAdjustment(userId);
  const adjustmentId = row.reference ?? '';
  const label = row.type_label;
  // Feed convention: debit (charge) is positive, credit/refund/comp negative.
  const sign = row.amount > 0 ? '+' : row.amount < 0 ? '−' : '';
  const amountText = `${sign}${formatCurrency(Math.abs(row.amount), row.currency)}`;

  const handleDelete = async () => {
    if (!adjustmentId) return;
    try {
      await del.mutateAsync(adjustmentId);
      showSuccess(
        'Entry deleted',
        `${label} of ${amountText} removed from the ledger.`,
      );
      onClose();
    } catch (err: any) {
      showError('Failed to delete entry', err?.message ?? 'Unknown error');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete ledger entry?</DialogTitle>
          <DialogDescription>
            This permanently removes the{' '}
            <span className="font-medium">{label.toLowerCase()}</span> of{' '}
            <span className="font-medium tabular-nums">{amountText}</span> from
            this contestant's manual ledger. This can't be undone — to keep an
            audit trail instead, record a reversing entry rather than deleting.
          </DialogDescription>
        </DialogHeader>

        {row.reason && (
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <div className="text-foreground">{row.reason}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Recorded {new Date(row.occurred_at).toLocaleString()}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={del.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={del.isPending}
          >
            {del.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Delete entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateAdjustmentModal({
  userId,
  orgs,
  defaultOrgId,
  onClose,
}: {
  userId: string;
  orgs: Array<{ id: string; name: string }>;
  defaultOrgId?: string;
  onClose: () => void;
}) {
  // Charge is the most common case (committee fronting an entry fee), so
  // default to it. The `direction` field becomes required only when the
  // admin picks `adjustment` — for the others we infer from the type.
  const [type, setType] = useState<AdjustmentType>('charge');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [direction, setDirection] = useState<'debit' | 'credit'>('debit');
  const [orgId, setOrgId] = useState(defaultOrgId || (orgs[0]?.id ?? ''));
  // Whether this charge blocks new event entries until paid (fines, etc.).
  const [blocksEntry, setBlocksEntry] = useState(false);

  const { showSuccess, showError } = useToast();
  const create = useCreateAdminAdjustment(userId);

  const meta = TYPE_META[type];
  // Blocking only applies to entries that ADD to what's owed: a Charge, or an
  // Adjustment running as a Debit. Credits/comps/refunds can never block.
  const isBlockable = type === 'charge' || (type === 'adjustment' && direction === 'debit');
  const numericAmount = parseFloat(amount);
  const amountValid = !isNaN(numericAmount) && numericAmount > 0;
  const reasonValid = reason.trim().length > 0;
  // Every ledger entry is org-specific — an org is always required (no
  // system-level entries, for any admin).
  const orgValid = !!orgId;
  const formValid = amountValid && reasonValid && orgValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValid) return;
    try {
      await create.mutateAsync({
        type,
        amount: numericAmount,
        reason: reason.trim(),
        notes: notes.trim() || undefined,
        direction: type === 'adjustment' ? direction : undefined,
        organizationId: orgId || undefined,
        blocksEventEntry: isBlockable ? blocksEntry : false,
      });
      showSuccess('Entry recorded', `${meta.label} of ${formatCurrency(numericAmount)} saved.`);
      onClose();
    } catch (err: any) {
      showError('Failed to record entry', err?.message ?? 'Unknown error');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record ledger entry</DialogTitle>
          <DialogDescription>
            Append a manual charge, refund, comp, credit, or adjustment to this contestant's
            ledger. The entry is part of the audit trail and can't be edited later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="adj-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as AdjustmentType)}>
              <SelectTrigger id="adj-type" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TYPE_META) as AdjustmentType[]).map((t) => (
                  <SelectItem key={t} value={t}>
                    {TYPE_META[t].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">{meta.description}</p>
          </div>

          {type === 'adjustment' && (
            <div>
              <Label>Direction</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  size="sm"
                  variant={direction === 'debit' ? 'default' : 'outline'}
                  onClick={() => setDirection('debit')}
                >
                  Debit (adds to amount due)
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={direction === 'credit' ? 'default' : 'outline'}
                  onClick={() => setDirection('credit')}
                >
                  Credit (reduces amount due)
                </Button>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="adj-amount">Amount (USD)</Label>
            <Input
              id="adj-amount"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1"
            />
          </div>

          {isBlockable && (
            <div className="flex items-start justify-between gap-3 rounded-md border p-3">
              <div className="min-w-0">
                <Label htmlFor="adj-blocks" className="cursor-pointer">
                  Block event entries until paid
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Use for fines / required fees. The contestant can't enter new
                  events for this organization until it's paid. Leave off for a
                  charge that shouldn't gate registration.
                </p>
              </div>
              <Switch
                id="adj-blocks"
                checked={blocksEntry}
                onCheckedChange={setBlocksEntry}
                className="mt-0.5 shrink-0"
              />
            </div>
          )}

          <div>
            <Label htmlFor="adj-org">
              Organization <span className="text-destructive">*</span>
            </Label>
            {orgs.length > 0 ? (
              <Select value={orgId || undefined} onValueChange={setOrgId}>
                <SelectTrigger id="adj-org" className="mt-1">
                  <SelectValue placeholder="Select an organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                This contestant isn't a member of any organization you
                administer — add them to one before recording a ledger entry.
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Every ledger entry belongs to one organization.
            </p>
          </div>

          <div>
            <Label htmlFor="adj-reason">Reason</Label>
            <Input
              id="adj-reason"
              placeholder="e.g. Committee fronted entry fee for Saturday performance"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="adj-notes">Notes (optional)</Label>
            <Textarea
              id="adj-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              rows={3}
              className="mt-1"
              placeholder="Internal note — check #, accounting reference, etc."
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!formValid || create.isPending}>
              {create.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Record {meta.label.toLowerCase()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Renders a Stripe object ID. When the ID matches a known Stripe prefix
 * (pi_, ch_, re_, …) we wrap it in an anchor that opens the Stripe
 * dashboard in a new tab — admins get a one-click jump to the source
 * record. Internal payment-row UUIDs fall through as plain text.
 */
function StripeReferenceCell({ id }: { id: string | null | undefined }) {
  if (!id) return <span>—</span>;
  const url = buildStripeDashboardUrl(id);
  if (!url) return <>{id}</>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline"
      title="View in Stripe dashboard"
    >
      {id}
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
    </a>
  );
}

// ─── Refund-via-Stripe modal ──────────────────────────────────────────────────
//
// Lets an admin issue a real Stripe refund against an existing payment in the
// user's history. The amount input is capped to the picked payment's
// `refundable_amount` (= captured amount minus completed refunds, computed
// server-side) so over-refunding is rejected client-side and the backend's
// live-Stripe ceiling check is a belt-and-suspenders second line of defense.

function RefundViaStripeModal({
  userId,
  payments,
  onClose,
}: {
  userId: string;
  payments: StripePaymentRow[];
  onClose: () => void;
}) {
  const [paymentId, setPaymentId] = useState(payments[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const { showSuccess, showError } = useToast();
  const refund = useCreateAdminStripeRefund(userId);

  const selected = payments.find((p) => p.id === paymentId);
  const refundableCap = selected?.refundable_amount ?? 0;
  const numericAmount = parseFloat(amount);
  const amountValid =
    !isNaN(numericAmount) &&
    numericAmount > 0 &&
    numericAmount <= refundableCap + 0.001; // tolerate float drift on the ceiling
  const reasonValid = reason.trim().length > 0 && reason.trim().length <= 500;
  const formValid = !!selected && amountValid && reasonValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValid || !selected) return;
    try {
      await refund.mutateAsync({
        paymentId: selected.id,
        amount: numericAmount,
        reason: reason.trim(),
      });
      showSuccess(
        'Refund issued',
        `${formatCurrency(numericAmount, selected.currency)} refunded to original payment method.`,
      );
      onClose();
    } catch (err: any) {
      // The backend returns a structured { error, code } shape; surface the
      // human message but keep the code for the operator if they need to grep
      // logs (e.g. STRIPE_REFUND_FAILED, REFUND_EXCEEDS_STRIPE_CEILING).
      showError('Refund failed', err?.message ?? 'Unknown error');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Refund via Stripe</DialogTitle>
          <DialogDescription>
            Issues a real Stripe refund against the selected payment. Money
            returns to the original payment method automatically — typically
            5–10 business days for cards, longer for ACH. The reason becomes
            the description on the Stripe-side refund record.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="refund-payment">Payment to refund</Label>
            <Select value={paymentId} onValueChange={setPaymentId}>
              <SelectTrigger id="refund-payment" className="mt-1">
                <SelectValue placeholder="Pick a payment…" />
              </SelectTrigger>
              <SelectContent>
                {payments.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {new Date(p.created_at).toLocaleDateString()}
                    {' • '}
                    {formatCurrency(p.amount, p.currency)}
                    {' • up to '}
                    {formatCurrency(p.refundable_amount, p.currency)}
                    {' refundable'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <p className="text-xs text-muted-foreground mt-1">
                Charge ID:{' '}
                <span className="font-mono">
                  <StripeReferenceCell
                    id={selected.provider_payment_id ?? selected.id}
                  />
                </span>
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="refund-amount">Refund amount</Label>
            <Input
              id="refund-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={refundableCap || undefined}
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Maximum: {formatCurrency(refundableCap, selected?.currency)}.
              {refundableCap < (selected?.amount ?? 0) && (
                <>
                  {' '}
                  (Reduced from the original{' '}
                  {formatCurrency(selected?.amount ?? 0, selected?.currency)} because some has
                  already been refunded.)
                </>
              )}
            </p>
          </div>

          <div>
            <Label htmlFor="refund-reason">
              Reason / description
              <span className="text-muted-foreground font-normal ml-1">(up to 500 chars)</span>
            </Label>
            <Textarea
              id="refund-reason"
              rows={3}
              maxLength={500}
              placeholder="e.g. Cancelled registration; partial refund minus transaction fee."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Stored on the local refund record AND passed to Stripe as the
              refund's description. Visible in the Stripe dashboard alongside
              the rest of the metadata.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={refund.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!formValid || refund.isPending}>
              {refund.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Issuing refund…
                </>
              ) : (
                'Issue refund'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
